import { iamLog } from '@identity/application/observability';
import type { SendVerificationLinkDto, SendVerificationLinkResult } from '@identity/application/dtos';
import type { IAuthApiPort, ICredentialSourcePort } from '@identity/application/ports';
import { extractErrorAttributes, retryAfterAttributes, subjectAttributes } from '@identity/application/utils';
import type { IdentityFlow } from '@identity/domain/types';

/**
 * Sends a verification link over a channel.
 *
 * The destination is never logged, neither complete nor partial: the account, the channel and
 * whether it was a resend are enough to diagnose a problem.
 *
 * The verification uuid is logged whole, and it is the only identifier of this domain that is. It is
 * an opaque the backend issued: it names no person and no number, and it is the key that ties a send
 * to the wait that followed it.
 */
export const sendVerificationLinkUseCase = (
  authApi: IAuthApiPort,
  credentials: ICredentialSourcePort,
  flow: IdentityFlow,
) => {
  const execute = async (dto: SendVerificationLinkDto): Promise<SendVerificationLinkResult> => {
    const { value, channel, isResend = false } = dto;
    const subject = { ...subjectAttributes(credentials.readUserId()), 'iam.flow': flow };

    try {
      const { verificationUuid } = await authApi.sendVerificationLink({ value, channel });

      iamLog.info('verificationLinkRequested', {
        ...subject,
        'iam.channel': channel,
        'iam.isResend': isResend,
        ...(verificationUuid ? { 'iam.verificationUuid': verificationUuid } : {}),
      });

      return { verificationUuid };
    } catch (error) {
      iamLog.error('verificationLinkRequested', {
        ...subject,
        ...extractErrorAttributes(error),
        ...retryAfterAttributes(error),
        'iam.channel': channel,
        'iam.isResend': isResend,
      });
      throw error;
    }
  };

  return { execute };
};
